import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Plus, Edit, Trash2, Save, X, Upload, RefreshCw, Eye, User, Phone, Mail, IdCard, MapPin } from 'lucide-react';
import { getBorrowers, deleteBorrower, updateBorrower } from '../services/api';
import { uploadMultipleImages } from '../services/cloudinary';
import Toast from '../components/Toast';
import ImageSourcePicker from '../components/ImageSourcePicker';
import { useLanguage } from '../contexts/LanguageContext';

const Borrowers = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');

  const [borrowers, setBorrowers] = useState([]);
  const [page, setPage] = useState(0);
  const [size] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [borrowerToDelete, setBorrowerToDelete] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingBorrower, setEditingBorrower] = useState(null);
  
  // Use ref to prevent duplicate API calls
  const loadingRef = useRef(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [editForm, setEditForm] = useState({
    fullName: '',
    fatherName: '',
    contactNumber: '',
    email: '',
    address: '',
    idProofType: '',
    idProofNumber: '',
    documentsUrl: ''
  });
  const [editSelectedPhotos, setEditSelectedPhotos] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [proofImages, setProofImages] = useState([]);

  const handleEditBorrower = (borrowerId) => {
    const borrower = borrowers.find(b => b.id === borrowerId);
    if (borrower) {
      setEditingBorrower(borrower);
      setEditForm({
        fullName: borrower.fullName || '',
        fatherName: borrower.fatherName || '',
        contactNumber: borrower.contactNumber || '',
        email: borrower.email || '',
        address: borrower.address || '',
        idProofType: borrower.idProofType || '',
        idProofNumber: borrower.idProofNumber || '',
        documentsUrl: borrower.documentsUrl || ''
      });
      setEditSelectedPhotos([]);
      setShowEditModal(true);
    }
  };

  const handleViewProof = (documentsUrl) => {
    if (!documentsUrl || documentsUrl === 'string') {
      setProofImages([]);
    } else {
      // Parse comma-separated URLs and filter out empty strings
      const urls = documentsUrl.split(',').map(url => url.trim()).filter(url => url && url !== 'string');
      // Take maximum 2 images
      setProofImages(urls.slice(0, 2));
    }
    setShowImageModal(true);
  };

  const handleEditPhotoChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Get existing photos count
    const existingPhotosCount = editForm.documentsUrl && editForm.documentsUrl !== 'string' 
      ? editForm.documentsUrl.split(',').filter(url => url.trim()).length 
      : 0;
    
    // Check if adding these files would exceed the limit
    if (existingPhotosCount + editSelectedPhotos.length + files.length > 2) {
      setToast({ 
        show: true, 
        message: t('borrowers.upload.maxPhotosError'), 
        type: 'error' 
      });
      return;
    }
    
    // Validate each file
    for (const file of files) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        setToast({ 
          show: true, 
          message: t('borrowers.upload.invalidFileType'), 
          type: 'error' 
        });
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        setToast({ 
          show: true, 
          message: `${file.name} - ${t('borrowers.upload.fileTooLarge')}`, 
          type: 'error' 
        });
        return;
      }
    }
    
    setEditSelectedPhotos(prev => [...prev, ...files]);
  };

  const removeEditPhoto = (index) => {
    setEditSelectedPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingPhoto = (index) => {
    const urls = editForm.documentsUrl.split(',').filter(url => url.trim());
    urls.splice(index, 1);
    setEditForm(prev => ({
      ...prev,
      documentsUrl: urls.join(',')
    }));
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingBorrower) return;

    try {
      let documentsUrl = editForm.documentsUrl || '';

      // Upload new photos if selected
      if (editSelectedPhotos.length > 0) {
        setIsUploading(true);
        try {
          const uploadedUrls = await uploadMultipleImages(editSelectedPhotos, { folder: 'borrowers' });
          
          // Combine existing URLs with new ones
          const existingUrls = documentsUrl && documentsUrl !== 'string' 
            ? documentsUrl.split(',').filter(url => url.trim()) 
            : [];
          
          const allUrls = [...existingUrls, ...uploadedUrls];
          documentsUrl = allUrls.join(',');
          
          setToast({ 
            show: true, 
            message: t('borrowers.upload.success'), 
            type: 'success' 
          });
        } catch (uploadError) {
          setToast({ 
            show: true, 
            message: `${t('borrowers.upload.failure')}: ${uploadError.message}`, 
            type: 'error' 
          });
          setIsUploading(false);
          return;
        } finally {
          setIsUploading(false);
        }
      }

      const updatedData = {
        ...editForm,
        documentsUrl: documentsUrl || 'string'
      };

      const result = await updateBorrower(editingBorrower.id, updatedData);
      if (result.success) {
        setToast({ show: true, message: t('borrowers.toast.updateSuccess'), type: 'success' });
        setShowEditModal(false);
        setEditingBorrower(null);
        setEditSelectedPhotos([]);
        loadBorrowers(); // Reload the list
      } else {
        setToast({ show: true, message: result.message || t('borrowers.toast.updateError'), type: 'error' });
      }
    } catch (error) {
      setToast({ show: true, message: t('borrowers.toast.updateError'), type: 'error' });
    }
  };

  const handleEditCancel = () => {
    setShowEditModal(false);
    setEditingBorrower(null);
    setEditForm({
      fullName: '',
      fatherName: '',
      contactNumber: '',
      email: '',
      address: '',
      idProofType: '',
      idProofNumber: '',
      documentsUrl: ''
    });
    setEditSelectedPhotos([]);
  };

  const handleDeleteClick = (borrowerId, borrowerName) => {
    setBorrowerToDelete({ id: borrowerId, name: borrowerName });
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (borrowerToDelete) {
      try {
        const result = await deleteBorrower(borrowerToDelete.id);
        if (result.success) {
          setToast({ show: true, message: t('borrowers.toast.deleteSuccess'), type: 'success' });
          loadBorrowers(); // Reload the list
        } else {
          setToast({ show: true, message: result.message || t('borrowers.toast.deleteError'), type: 'error' });
        }
      } catch (error) {
        setToast({ show: true, message: t('borrowers.toast.deleteError'), type: 'error' });
      }
    }
    setShowDeleteModal(false);
    setBorrowerToDelete(null);
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setBorrowerToDelete(null);
  };

  const MIN_SEARCH_CHARS = 2;
  const searchTermTrimmed = searchTerm.trim();
  const searchTooShort = searchTermTrimmed.length > 0 && searchTermTrimmed.length < MIN_SEARCH_CHARS;

  const loadBorrowers = useCallback(async () => {
    // Prevent duplicate calls
    if (loadingRef.current) return;
    
    loadingRef.current = true;
    setLoading(true);
    try {
      const searchQuery = debouncedSearch.length >= MIN_SEARCH_CHARS ? debouncedSearch : '';
      const res = await getBorrowers({
        page,
        size,
        sortBy: 'createdAt',
        sortDir: 'desc',
        search: searchQuery
      });
      if (res.success) {
        const payload = res.data;
        const list = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.content)
            ? payload.content
            : [];
        setBorrowers(list);
        setTotalPages(payload?.totalPages || 0);
        setTotalElements(payload?.totalElements || list.length);
      } else {
        setToast({ show: true, message: res.message || t('borrowers.toast.loadError'), type: 'error' });
        setBorrowers([]);
        setTotalPages(0);
        setTotalElements(0);
      }
    } catch (e) {
      setToast({ show: true, message: e.message || t('borrowers.toast.loadError'), type: 'error' });
      setBorrowers([]);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size, debouncedSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const next = searchTerm.trim();
      // Only update search when empty or meets min length (avoid 1-char API hits)
      if (next.length === 0 || next.length >= MIN_SEARCH_CHARS) {
        setDebouncedSearch(next);
        setPage(0);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    loadBorrowers();
  }, [loadBorrowers]);

  return (
    <div>
      <div className="card list-page-card">
        <div className="card-header list-page-header">
          <div className="list-filters">
            <div className="list-filter-search">
              <div className="list-filter-search-control">
                <Search size={18} className="list-filter-search-icon" />
                <input
                  type="text"
                  className={`search-input${searchTooShort ? ' search-input--error' : ''}`}
                  placeholder={t('borrowers.searchPlaceholderTable')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    type="button"
                    className="list-filter-search-clear"
                    onClick={() => {
                      setSearchTerm('');
                      setDebouncedSearch('');
                      setPage(0);
                    }}
                    aria-label="Clear search"
                    title="Clear"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
              {searchTooShort && (
                <p className="list-filter-search-error">{t('loans.messages.searchMinChars')}</p>
              )}
            </div>
            <Link to="/borrowers/create" className="btn btn-primary list-filter-cta">
              <Plus size={16} />
              {t('borrowers.addBorrower')}
            </Link>
          </div>
        </div>

        <div className="table-container desktop-table-only">
          <table className="table">
            <thead>
              <tr>
                <th>{t('borrowers.tableHeaders.index')}</th>
                <th>{t('borrowers.tableHeaders.name')}</th>
                <th>{t('borrowers.tableHeaders.fatherName')}</th>
                <th>{t('borrowers.tableHeaders.phone')}</th>
                <th>{t('borrowers.tableHeaders.email')}</th>
                <th>{t('borrowers.tableHeaders.address')}</th>
                <th>{t('borrowers.tableHeaders.idProofType')}</th>
                <th>{t('borrowers.tableHeaders.idNumber')}</th>
                <th>{t('borrowers.tableHeaders.viewId')}</th>
                <th>{t('borrowers.tableHeaders.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" style={{ padding: '2rem', textAlign: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', color: '#6b7280' }}>
                      <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite' }} />
                      <span>{t('borrowers.loadingBorrowers')}</span>
                    </div>
                  </td>
                </tr>
              ) : borrowers.length === 0 ? (
                <tr>
                  <td colSpan="10" className="error-message">
                    {t('borrowers.noBorrowersFound')}
                  </td>
                </tr>
              ) : (
                borrowers.map((borrower, index) => (
                  <tr 
                    key={borrower.id || index}
                    style={{ cursor: 'pointer' }}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td 
                      style={{ fontWeight: '500', textAlign: 'center' }}
                      onClick={() => navigate(`/borrowers/${borrower.id}`)}
                    >
                      {index + 1}
                    </td>
                    <td onClick={() => navigate(`/borrowers/${borrower.id}`)}>
                      {borrower.fullName}
                    </td>
                    <td
                      onClick={() => navigate(`/borrowers/${borrower.id}`)}
                      style={{
                        color: borrower.fatherName ? '#374151' : '#9ca3af',
                        fontStyle: borrower.fatherName ? 'normal' : 'italic'
                      }}
                    >
                      {borrower.fatherName || t('loans.labels.serialMissing') || '-'}
                    </td>
                    <td onClick={() => navigate(`/borrowers/${borrower.id}`)}>
                      {borrower.contactNumber}
                    </td>
                    <td onClick={() => navigate(`/borrowers/${borrower.id}`)}>
                      {borrower.email}
                    </td>
                    <td onClick={() => navigate(`/borrowers/${borrower.id}`)}>
                      {borrower.address}
                    </td>
                    <td onClick={() => navigate(`/borrowers/${borrower.id}`)}>
                      {borrower.idProofType}
                    </td>
                    <td onClick={() => navigate(`/borrowers/${borrower.id}`)}>
                      {borrower.idProofNumber}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {borrower.documentsUrl && borrower.documentsUrl !== 'string' ? (
                        <button
                          className="btn btn-outline"
                          style={{
                            padding: '0.25rem',
                            fontSize: '0.75rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '32px',
                            height: '32px',
                            cursor: 'pointer',
                            margin: '0 auto'
                          }}
                          onClick={() => handleViewProof(borrower.documentsUrl)}
                          title={t('borrowers.tooltips.viewId')}
                        >
                          <Eye size={14} />
                        </button>
                      ) : (
                        <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>{t('borrowers.tableLabels.noProof')}</span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                         <button 
                           className="btn btn-outline" 
                           style={{ 
                             padding: '0.25rem', 
                             fontSize: '0.75rem',
                             display: 'flex',
                             alignItems: 'center',
                             justifyContent: 'center',
                             minWidth: '32px',
                             height: '32px',
                             cursor: 'pointer'
                           }}
                           onClick={() => handleEditBorrower(borrower.id)}
                           title={t('borrowers.tooltips.edit')}
                         >
                           <Edit size={14} />
                         </button>
                         <button 
                           className="btn btn-outline" 
                           style={{ 
                             padding: '0.25rem', 
                             fontSize: '0.75rem',
                             display: 'flex',
                             alignItems: 'center',
                             justifyContent: 'center',
                             minWidth: '32px',
                             height: '32px',
                             color: '#ef4444',
                             borderColor: '#ef4444',
                             cursor: 'pointer'
                           }}
                           onClick={() => handleDeleteClick(borrower.id, borrower.fullName)}
                           title={t('borrowers.tooltips.delete')}
                         >
                           <Trash2 size={14} />
                         </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="mobile-card-list">
          {loading ? (
            <div className="mobile-card-empty">
              <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite' }} />
              <span>{t('borrowers.loadingBorrowers')}</span>
            </div>
          ) : borrowers.length === 0 ? (
            <div className="mobile-card-empty">{t('borrowers.noBorrowersFound')}</div>
          ) : (
            borrowers.map((borrower, index) => (
              <article
                key={borrower.id || index}
                className="loan-mobile-card"
                onClick={() => navigate(`/borrowers/${borrower.id}`)}
              >
                <div className="loan-card-top">
                  <div className="loan-card-id">
                    <span className="loan-card-icon">
                      <User size={16} />
                    </span>
                    <div className="loan-card-id-stack">
                      <span className="loan-card-id-text">{borrower.fullName || t('loans.labels.na')}</span>
                      {borrower.fatherName && (
                        <span className="loan-card-subtitle">{borrower.fatherName}</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="loan-card-body">
                  <div className="loan-card-customer">
                    <div className="loan-card-meta-row">
                      <Phone size={15} />
                      <span>{borrower.contactNumber || t('loans.labels.na')}</span>
                    </div>
                    <div className="loan-card-meta-row">
                      <Mail size={15} />
                      <span>{borrower.email || t('loans.labels.na')}</span>
                    </div>
                  </div>
                  <div className="loan-card-principal">
                    <span className="loan-card-principal-label">{t('borrowers.tableHeaders.idProofType')}</span>
                    <span className="loan-card-principal-value" style={{ fontSize: '0.95rem' }}>
                      {borrower.idProofType || t('loans.labels.na')}
                    </span>
                  </div>
                </div>

                <div className="loan-card-footer">
                  <div className="loan-card-footer-item">
                    <IdCard size={15} />
                    <div>
                      <span className="loan-card-footer-label">{t('borrowers.tableHeaders.idNumber')}</span>
                      <span className="loan-card-footer-value">{borrower.idProofNumber || t('loans.labels.na')}</span>
                    </div>
                  </div>
                  <div className="loan-card-footer-item">
                    <MapPin size={15} />
                    <div>
                      <span className="loan-card-footer-label">{t('borrowers.tableHeaders.address')}</span>
                      <span className="loan-card-footer-value">{borrower.address || t('loans.labels.na')}</span>
                    </div>
                  </div>
                  <div className="loan-card-footer-actions" onClick={(e) => e.stopPropagation()}>
                    {borrower.documentsUrl && borrower.documentsUrl !== 'string' && (
                      <button
                        type="button"
                        className="loan-card-action is-view"
                        onClick={() => handleViewProof(borrower.documentsUrl)}
                        aria-label={t('borrowers.tooltips.viewId')}
                        title={t('borrowers.tooltips.viewId')}
                      >
                        <Eye size={16} />
                      </button>
                    )}
                    <button
                      type="button"
                      className="loan-card-action is-edit"
                      onClick={() => handleEditBorrower(borrower.id)}
                      aria-label={t('borrowers.tooltips.edit')}
                      title={t('borrowers.tooltips.edit')}
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      type="button"
                      className="loan-card-delete"
                      onClick={() => handleDeleteClick(borrower.id, borrower.fullName)}
                      aria-label={t('borrowers.tooltips.delete')}
                      title={t('borrowers.tooltips.delete')}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </article>
            ))
          )}
        </div>

        {/* Pagination — only when more than one page */}
        {totalPages > 1 && (
          <div className="list-pagination">
            <button
              type="button"
              className="btn btn-outline list-pagination-btn"
              disabled={page === 0 || loading}
              onClick={() => setPage(p => Math.max(p - 1, 0))}
            >
              {t('common.prev')}
            </button>
            <span className="list-pagination-info">
              {t('common.page')} {page + 1} / {totalPages}
              <span className="list-pagination-total">
                ({totalElements} {t('common.total')})
              </span>
            </span>
            <button
              type="button"
              className="btn btn-outline list-pagination-btn"
              disabled={page + 1 >= totalPages || loading}
              onClick={() => setPage(p => p + 1)}
            >
              {t('common.next')}
            </button>
          </div>
        )}
      </div>
       {toast.show && (
         <Toast 
           message={toast.message}
           type={toast.type}
           duration={4000}
           onClose={() => setToast(prev => ({ ...prev, show: false }))}
         />
       )}

       {/* Delete Confirmation Modal */}
       {showDeleteModal && (
         <div className="borrower-modal-overlay">
           <div className="borrower-modal borrower-modal-sm">
             <h3 className="borrower-modal-title" style={{ marginBottom: '1rem' }}>{t('borrowers.deleteModal.title')}</h3>
             <p className="borrower-modal-hint" style={{ marginBottom: '1.5rem', fontSize: '0.95rem' }}>
               {t('borrowers.deleteModal.messageStart')} <strong style={{ color: 'var(--text-primary)' }}>{borrowerToDelete?.name}</strong>?
             </p>
             <div className="borrower-modal-actions">
               <button
                 className="btn btn-outline"
                 onClick={handleDeleteCancel}
                 style={{ padding: '0.5rem 1rem' }}
               >
                 {t('borrowers.deleteModal.cancel')}
               </button>
               <button
                 className="btn"
                 onClick={handleDeleteConfirm}
                 style={{ 
                   padding: '0.5rem 1rem',
                   backgroundColor: '#ef4444',
                   color: 'white',
                   border: 'none'
                 }}
               >
                 {t('borrowers.deleteModal.confirm')}
               </button>
             </div>
           </div>
         </div>
       )}

       {/* Edit Modal */}
       {showEditModal && (
         <div className="borrower-modal-overlay">
           <div className="borrower-modal">
             <div className="borrower-modal-header">
               <h3 className="borrower-modal-title">{t('borrowers.editModal.title')}</h3>
               <button
                 type="button"
                 onClick={handleEditCancel}
                 className="borrower-modal-close"
                 title={t('borrowers.editModal.close')}
               >
                 <X size={20} />
               </button>
             </div>

             <form onSubmit={handleEditSubmit}>
               <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '0.5rem' }}>
                 <div className="form-group">
                   <label className="form-label">{t('borrowers.form.fullNameLabel')}</label>
                   <input
                     type="text"
                     name="fullName"
                     className="form-input"
                     value={editForm.fullName}
                     onChange={handleEditFormChange}
                     required
                   />
                 </div>
                 <div className="form-group">
                   <label className="form-label">{t('borrowers.form.fatherNameLabel')}</label>
                   <input
                     type="text"
                     name="fatherName"
                     className="form-input"
                     value={editForm.fatherName}
                     onChange={handleEditFormChange}
                     required
                   />
                 </div>
                 <div className="form-group">
                   <label className="form-label">{t('borrowers.form.contactNumberLabel')}</label>
                   <input
                     type="tel"
                     name="contactNumber"
                     className="form-input"
                     value={editForm.contactNumber}
                     onChange={handleEditFormChange}
                     required
                   />
                 </div>
               </div>

               <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '0.5rem' }}>
                 <div className="form-group">
                   <label className="form-label">{t('borrowers.form.idProofTypeLabel')}</label>
                   <select
                     name="idProofType"
                     className="form-input"
                     value={editForm.idProofType}
                     onChange={handleEditFormChange}
                     required
                   >
                     <option value="">{t('borrowers.form.selectIdProofType')}</option>
                     <option value="Aadhaar Card">Aadhaar Card</option>
                     <option value="PAN Card">PAN Card</option>
                     <option value="Driving License">Driving License</option>
                     <option value="Voter ID">Voter ID</option>
                     <option value="Passport">Passport</option>
                   </select>
                 </div>
                 <div className="form-group">
                   <label className="form-label">{t('borrowers.form.idProofNumberLabel')}</label>
                   <input
                     type="text"
                     name="idProofNumber"
                     className="form-input"
                     value={editForm.idProofNumber}
                     onChange={handleEditFormChange}
                     required
                   />
                 </div>
                 <div className="form-group">
                   <label className="form-label">{t('borrowers.form.emailLabel')}</label>
                   <input
                     type="email"
                     name="email"
                     className="form-input"
                     value={editForm.email}
                     onChange={handleEditFormChange}
                   />
                 </div>
               </div>

               <div className="form-group" style={{ marginBottom: '1rem' }}>
                 <label className="form-label">{t('borrowers.form.addressLabel')}</label>
                 <textarea
                   name="address"
                   className="form-input form-textarea"
                   value={editForm.address}
                   onChange={handleEditFormChange}
                   rows={5}
                   style={{ minHeight: '130px', maxHeight: '130px' }}
                   required
                 />
               </div>

              {/* Photo Upload Section */}
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label className="form-label">{t('borrowers.form.uploadTitle')}</label>
                
                {/* Existing Photos */}
                {editForm.documentsUrl && editForm.documentsUrl !== 'string' && (() => {
                  const existingUrls = editForm.documentsUrl.split(',').filter(url => url.trim());
                  
                  return existingUrls.length > 0 && (
                    <div style={{ marginBottom: '1rem' }}>
                      <div className="borrower-modal-hint">
                        {t('borrowers.form.currentPhotosTitle')}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
                        {existingUrls.map((url, index) => (
                          <div key={index} style={{ position: 'relative' }}>
                            <img
                              src={url}
                              alt={`ID proof document ${index + 1}`}
                              className="borrower-modal-photo"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => removeExistingPhoto(index)}
                              className="borrower-modal-photo-remove"
                              title={t('borrowers.form.removePhoto')}
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Upload New Photos */}
                {(() => {
                  const existingCount = editForm.documentsUrl && editForm.documentsUrl !== 'string' 
                    ? editForm.documentsUrl.split(',').filter(url => url.trim()).length 
                    : 0;
                  const totalCount = existingCount + editSelectedPhotos.length;
                  const canAddMore = totalCount < 2;

                  return canAddMore && (
                    <div 
                      className={`borrower-modal-upload${editSelectedPhotos.length > 0 ? ' has-files' : ''}`}
                      style={{
                        opacity: !canAddMore ? 0.6 : 1,
                        marginBottom: editSelectedPhotos.length > 0 ? '1rem' : 0
                      }}
                    >
                      <Upload size={24} className="borrower-modal-upload-icon" />
                      <div className="borrower-modal-hint" style={{ marginBottom: '0.75rem' }}>
                        {editSelectedPhotos.length > 0 
                          ? `${editSelectedPhotos.length} ${t('borrowers.upload.selectedSuffix')}`
                          : t('borrowers.form.uploadHint')}
                      </div>
                      <ImageSourcePicker
                        multiple
                        disabled={isUploading || !canAddMore}
                        onChange={handleEditPhotoChange}
                        galleryLabel={t('common.gallery')}
                        cameraLabel={t('common.camera')}
                        hint={t('borrowers.form.uploadLimit')}
                      />
                    </div>
                  );
                })()}

                {/* New Selected Photos */}
                {editSelectedPhotos.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
                    {editSelectedPhotos.map((photo, index) => (
                      <div key={index} style={{ position: 'relative' }}>
                        <img
                          src={URL.createObjectURL(photo)}
                          alt={`New ID proof upload ${index + 1}`}
                          className="borrower-modal-photo is-new"
                        />
                        <button
                          type="button"
                          onClick={() => removeEditPhoto(index)}
                          className="borrower-modal-photo-remove"
                          title={t('borrowers.form.removePhoto')}
                        >
                          <X size={14} />
                        </button>
                        <div className="borrower-modal-photo-caption">
                          {`${t('borrowers.form.newPhotoLabel')} - ${(photo.size / 1024).toFixed(0)} KB`}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="borrower-modal-actions">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={handleEditCancel}
                  style={{ padding: '0.5rem 1rem' }}
                  disabled={isUploading}
                >
                  {t('borrowers.buttons.cancel')}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ 
                    padding: '0.5rem 1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  disabled={isUploading}
                >
                  <Save size={16} />
                  {isUploading ? t('borrowers.status.uploading') : t('borrowers.buttons.update')}
                </button>
              </div>
             </form>
           </div>
         </div>
       )}

      {/* Image Proof Modal */}
      {showImageModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '2rem'
          }}
          onClick={() => setShowImageModal(false)}
        >
          <div
            style={{
              position: 'relative',
              maxWidth: '90%',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              alignItems: 'center'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowImageModal(false)}
              style={{
                position: 'absolute',
                top: '-2.5rem',
                right: '0',
                background: 'white',
                color: '#374151',
                border: 'none',
                borderRadius: '50%',
                width: '2.5rem',
                height: '2.5rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem',
                fontWeight: 'bold',
                zIndex: 10000
              }}
            >
              <X size={20} />
            </button>

            {/* Images Container */}
            {proofImages.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem', width: '100%', maxWidth: '1200px', justifyContent: 'center' }}>
                {proofImages.map((imageUrl, index) => (
                  <div
                    key={index}
                    style={{
                      position: 'relative',
                      flex: '1',
                      minWidth: '0',
                      maxWidth: '600px',
                      background: 'white',
                      borderRadius: '0.5rem',
                      padding: '0.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <img
                      src={imageUrl}
                      alt={`ID proof document ${index + 1}`}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '70vh',
                        objectFit: 'contain',
                        borderRadius: '0.375rem',
                        width: '100%',
                        height: 'auto'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        if (e.target.nextSibling) {
                          e.target.nextSibling.style.display = 'flex';
                        }
                      }}
                    />
                    <div
                      style={{
                        display: 'none',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '200px',
                        color: '#9ca3af',
                        fontSize: '0.875rem'
                      }}
                    >
                      {t('borrowers.imageModal.failedToLoad')}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{
                background: 'white',
                padding: '3rem',
                borderRadius: '0.5rem',
                color: '#9ca3af',
                fontSize: '0.875rem',
                textAlign: 'center'
              }}>
                {t('borrowers.imageModal.noImages')}
              </div>
            )}
          </div>
        </div>
      )}
     </div>
   );
 };

export default Borrowers;
